import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useState,
} from "react";
import NextImage, { ImageProps } from "next/future/image";
import { getImageSrc } from "~lib";
import { Box, SystemProps, system } from "flicket-ui";
import { ExtendedFile, MetaData } from "~graphql/sdk";
import styled from "styled-components";
import { omit, pick } from "@styled-system/props";
import { ImageWrapper } from "../common.ImageWrapper";

export interface ImageWrapperProps extends Omit<
  ImageProps,
  "src" | "width" | "height" | "color"
> {
  src?: string;
  image?: ExtendedFile;
  style?: React.CSSProperties;
  fallback?: string;
  metaData?: MetaData;
  disableLoadingState?: boolean;
}

export const StyledDiv = styled.div<SystemProps>`
  overflow: hidden;
  position: relative;
  ${system}
`;

// This component uses next/image under the hood to render the image.
// The component works with images that have metadata, or it reads the
// width of the current container and uses that to supply to next/image
const Image: FC<ImageWrapperProps & SystemProps> = ({
  image,
  src,
  alt,
  style = {},
  fallback = "",
  disableLoadingState = false,
  ...rest
}) => {
  const [contentSize, setContentSize] = useState<MetaData>(image?.metaData);
  const [isLoading, setIsLoading] = useState(true);

  const imageSrc = src ?? getImageSrc(image, fallback);

  useEffect(() => {
    setIsLoading(true);
  }, [imageSrc]);

  const wrapperCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && !contentSize) {
        setContentSize({
          width: node.offsetWidth,
          height: node.offsetHeight,
        });
      }
    },
    [contentSize]
  );

  if (!imageSrc) {
    console.error("Image component received null or undefined source", {
      image,
      src,
      fallback,
    });
    return null;
  }

  return (
    <StyledDiv
      ref={wrapperCallback}
      css={{
        aspectRatio: !contentSize ? 5 / 2 : undefined,
      }}
      height="auto"
      {...pick(rest)}
    >
      {contentSize && (
        <>
          {!disableLoadingState && isLoading && <ImageSkeletonUnderlay />}
          <NextImage
            width={contentSize.width}
            height={contentSize.height}
            style={{
              width: "100%",
              height: "auto",
              isolation: "isolate", // Add a stacking context to prevent blending with the skeleton
              ...style,
            }}
            src={imageSrc}
            alt={alt}
            onError={(e) => {
              console.error("Image failed to load:", imageSrc, e);
              setIsLoading(false);
            }}
            {...omit(rest)}
            onLoadingComplete={(img) => {
              setIsLoading(false);
              rest.onLoadingComplete?.(img);
            }}
          />
        </>
      )}
    </StyledDiv>
  );
};

interface IFixedSizeImageProps extends Omit<
  ImageWrapperProps,
  "objectFit" | "objectPosition" | "width" | "height"
> {
  objectFit?: CSSProperties["objectFit"];
  objectPosition?: CSSProperties["objectPosition"];
  height: number;
  width: number;
}

function FixedSizeImage({
  image,
  src,
  alt,
  fallback = "",
  width,
  height,
  objectPosition = "center",
  objectFit = "contain",
  disableLoadingState = false,
  ...props
}: IFixedSizeImageProps & SystemProps<typeof ImageWrapper>) {
  const [isLoading, setIsLoading] = useState(true);
  const imageSrc = src ?? getImageSrc(image, fallback);

  useEffect(() => {
    setIsLoading(true);
  }, [imageSrc]);

  if (!imageSrc) {
    console.error("FixedSizeImage received null or undefined source", {
      image,
      src,
      fallback,
    });
    return null;
  }

  return (
    <ImageWrapper width={width} height={height} {...pick(props)}>
      {!disableLoadingState && isLoading && <ImageSkeletonUnderlay />}
      <NextImage
        src={imageSrc}
        alt={alt}
        fill={true}
        style={{ objectFit, objectPosition, isolation: "isolate" }} // Add a stacking context to prevent blending with the skeleton
        onError={(e) => {
          console.error("Fixed size image failed to load:", imageSrc, e);
          setIsLoading(false);
        }}
        {...omit(props)}
        onLoadingComplete={(img) => {
          setIsLoading(false);
          props.onLoadingComplete?.(img);
        }}
      />
    </ImageWrapper>
  );
}

export interface IFixedRatioImageProps extends Omit<
  IFixedSizeImageProps,
  "width" | "height"
> {
  /** aspect ratio expressed a `"width / height"` */
  aspectRatio: CSSProperties["aspectRatio"];
  width?: SystemProps["width"];
  height?: SystemProps["height"];
  useNearFitCover?: boolean;
  nearFitThresholdPercent?: number;
}

function FixedRatioImage({
  image,
  src,
  metaData,
  alt,
  fallback = "",
  aspectRatio,
  objectPosition = "center",
  objectFit = "cover",
  useNearFitCover = false,
  nearFitThresholdPercent = 5,
  disableLoadingState = false,
  ...props
}: IFixedRatioImageProps & Omit<SystemProps, "objectFit" | "objectPosition">) {
  const [isLoading, setIsLoading] = useState(true);

  const imageSrc = src ?? getImageSrc(image, fallback);

  useEffect(() => {
    setIsLoading(true);
  }, [imageSrc]);

  if (!imageSrc) {
    console.error("FixedRatioImage received null or undefined source", {
      image,
      src,
      fallback,
    });
    return null;
  }

  const fitType = (() => {
    if (useNearFitCover) {
      return getNearFitType(
        metaData ?? image?.metaData,
        aspectRatio,
        nearFitThresholdPercent,
        objectFit
      );
    }

    return objectFit;
  })();

  return (
    <Box
      position="relative"
      overflow="hidden"
      css={{
        aspectRatio,
      }}
      height="auto"
      {...pick(props)}
    >
      {!disableLoadingState && isLoading && <ImageSkeletonUnderlay />}

      <NextImage
        src={imageSrc}
        alt={alt}
        fill={true}
        style={{
          objectFit: fitType,
          objectPosition,
          isolation: "isolate", // Add a stacking context to prevent blending with the skeleton
        }}
        onError={(e) => {
          console.error("Fixed ratio image failed to load:", imageSrc, e);
          setIsLoading(false);
        }}
        {...omit(props)}
        onLoadingComplete={(img) => {
          setIsLoading(false);
          props.onLoadingComplete?.(img);
        }}
      />
    </Box>
  );
}

export default Image;
export { FixedSizeImage, FixedRatioImage };

function getImageAspectRatio(metaData: MetaData) {
  if (!metaData?.width || !metaData?.height) return;

  const { width, height } = metaData;

  if (width > height) {
    return width / height;
  }
}

function calculatePercentageDifference(value1, value2) {
  const difference = value1 - value2;
  return Math.abs((difference / value2) * 100);
}

function getNearFitType(
  metaData: MetaData,
  aspectRatio: CSSProperties["aspectRatio"],
  thresholdPercent: number,
  defaultFit: CSSProperties["objectFit"] = "cover"
) {
  const imageAspectRatio = getImageAspectRatio(metaData);
  // `eval` idea taken from https://stackoverflow.com/a/7142720
  const parsedAspectRatio = eval(aspectRatio as string) as number;
  const percentageDiff = calculatePercentageDifference(
    imageAspectRatio,
    parsedAspectRatio
  );

  return percentageDiff < thresholdPercent ? "cover" : defaultFit;
}

{
  /* Creates the background loading effect */
}
function ImageSkeletonUnderlay() {
  return (
    <Box
      position="absolute"
      top={0}
      bottom={0}
      left={0}
      right={0}
      className="react-loading-skeleton"
    />
  );
}
